import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Sparkle, CaretDown, CaretUp, Envelope, Phone, User, Spinner } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { useSeo } from "../hooks/useSeo";
import PriceCalculator from "../components/PriceCalculator";

const benefits = [
  "Professioneller Arbeitsplatz im Premium Salon",
  "Hochwertige Salonatmosphäre",
  "Austausch mit anderen Friseuren",
  "Infrastruktur & Ausstattung vorhanden",
  "Kein Investitionsrisiko",
  "Keine Fixkosten wie bei eigenem Salon",
  "Flexible Arbeitszeiten",
  "Networking & Inspiration",
  "Education und Events als Option",
];

const includedInPrice = [
  "Bedienplatz im Premium Salon",
  "Waschplatz",
  "Handtücher",
  "WLAN",
  "Rezeption",
  "Buchungssystem",
  "Kassensystem",
  "Reinigung",
  "Backbar (Shampoo, Conditioner & Masken)",
  "Musik & Salonatmosphäre",
  "Social-Media-Unterstützung",
];

const steps = [
  {
    title: "Anfrage senden",
    description: "Fülle einfach unser kurzes Anfrageformular aus und erzähle uns ein paar Dinge über dich, deine Arbeit und wie du dir dein Coworking vorstellst.",
  },
  {
    title: "Kennenlernen im Salon",
    description: "Wir melden uns bei dir und vereinbaren ein persönliches Kennenlernen im JS Blond Salon. Dabei zeigen wir dir den Coworking Space, beantworten alle Fragen und schauen gemeinsam, ob das Konzept zu dir passt.",
  },
  {
    title: "Optional: Probetag",
    description: "Du möchtest den Salon erst einmal live erleben? Kein Problem – buche einfach einen Probetag gegen eine kleine Tagespauschale und arbeite einen Tag lang im Salon mit deinen Kunden.",
  },
  {
    title: "Dein Coworking-Modell wählen",
    description: "Gemeinsam legen wir fest, an welchen Tagen du arbeiten möchtest und welches Modell am besten zu deinem Business passt.",
  },
  {
    title: "Start mit deinen Kunden",
    description: "Nach der Vertragsunterzeichnung bekommst du eine Einführung in alle Abläufe und kannst direkt mit deinen eigenen Kunden starten. Ab jetzt arbeitest du selbstständig in einem professionellen Salonumfeld – ohne den Stress eines eigenen Salons.",
  },
];

const faqs = [
  {
    question: "Was kostet mich das genau?",
    answer: "Über unseren Kalkulator kannst du ganz einfach berechnen, was dich dein Bedienplatz monatlich kostet. Du entscheidest selbst, wie viele Tage pro Woche du arbeiten möchtest. Je mehr Tage du buchst, desto günstiger wird der Preis pro Tag. Zu Beginn hinterlegst du eine Kaution in Höhe des zweifachen Monatswertes deiner geplanten Arbeitstage. Das Beste: Du kannst jeden Monat flexibel anpassen, an wie vielen Tagen du arbeiten möchtest.",
  },
  {
    question: "Was ist im Preis enthalten?",
    answer: "Im Coworking-Preis ist bereits sehr viel enthalten, damit du dich komplett auf deine Arbeit konzentrieren kannst: Bedienplatz im Premium Salon, Waschplatz, Handtücher, WLAN, Rezeption, Buchungssystem, Kassensystem, Reinigung, Backbar (Shampoo, Conditioner & Masken), Musik & Salonatmosphäre, Social-Media-Unterstützung. Farbe und Blondierung werden nach Verbrauch separat berechnet. Du bringst im Grunde nur deine Kunden und dein Können mit.",
  },
  {
    question: "Bin ich wirklich komplett selbstständig oder gibt es Vorgaben?",
    answer: "Ja – du arbeitest vollständig selbstständig. Das bedeutet: Du bestimmst deine Preise, du bestimmst deine Dienstleistungen und Pakete. Dabei hast du deine eigenen Kunden, Terminbuchung und Kundenkartei, auf die niemand Zugriff hat. Einzige Regel: Deine Preise dürfen maximal 10% vom Preisniveau von JS Blond abweichen, damit das Preisgefüge im Salon für alle fair bleibt.",
  },
  {
    question: "Wie flexibel ist das Modell wirklich?",
    answer: "Sehr flexibel. Du kannst jeden Monat selbst entscheiden, an welchen Tagen du arbeiten möchtest. Die Tage sollten grundsätzlich festgelegt sein, damit wir den Ablauf für alle Coworker gut organisieren können. Natürlich sind Sonderlösungen jederzeit möglich, wenn es einmal Ausnahmen gibt.",
  },
  {
    question: "Welche Zielgruppe passt zum Salon?",
    answer: "JS Blond ist ein hochwertiger Damensalon mit Premium-Atmosphäre. Unsere Kundschaft ist sehr gemischt: junge Frauen, Mamas, Business-Kundinnen, Selbstständige, Geschäftsführerinnen. Kurz gesagt: Bei uns fühlt sich jeder wohl, der Wert auf Qualität legt. Wir sind offiziell ein Damensalon, haben aber selbstverständlich nichts dagegen, wenn auch mal ein Mann zum Haarschnitt vorbeikommt.",
  },
  {
    question: "Muss ich meine Kunden selbst mitbringen?",
    answer: "Ja – grundsätzlich arbeitest du mit deinen eigenen Kunden. Gleichzeitig unterstützen wir dich beim Aufbau und Wachstum deiner Kundschaft durch: Social Media Support, Events im Salon, gemeinsame Marketingaktionen, Sichtbarkeit im Salon. Wir sehen uns als Partner für dein Wachstum.",
  },
  {
    question: "Wie läuft der Start konkret ab?",
    answer: "Der Einstieg ist ganz unkompliziert: 1. Du füllst unser kurzes Anfrageformular aus. 2. Wir melden uns bei dir und vereinbaren ein Kennenlernen im Salon. 3. Du bekommst einen Einblick in den Coworking Space. 4. Optional kannst du einen Probetag gegen Tagespauschale buchen. 5. Wenn alles passt, unterschreiben wir den Vertrag und du bekommst eine Einführung in alle Abläufe. Danach kannst du direkt mit deinen Kunden starten.",
  },
  {
    question: "Gibt es Konkurrenz im selben Raum?",
    answer: "Natürlich bieten mehrere Friseure ähnliche Dienstleistungen an. Trotzdem arbeitet jeder komplett unabhängig mit eigener Kundenkartei, eigener Terminbuchung und eigener Kasse. Es gibt keine Überschneidungen oder Zugriff auf fremde Kundendaten. Wir sehen uns als neue Community, nicht als Piranha-Becken.",
  },
  {
    question: "Gibt es Stauraum für mein Material?",
    answer: "Ja. Wenn du mindestens 4 Tage im Monat im Salon arbeitest, bekommst du einen eigenen Spind, in dem du dein Material und deine Produkte lagern kannst. Zusätzlich gibt es einen großen Personalraum, den alle gemeinsam nutzen. Auch unser Garten mit Terrasse steht dir für deine Pausen zur Verfügung.",
  },
  {
    question: "Muss ich mit den Produkten im Salon arbeiten?",
    answer: "Nein – du kannst grundsätzlich mit deinen eigenen Produkten arbeiten. Diese kannst du in deinem persönlichen Spind aufbewahren. Wenn du möchtest, kannst du aber auch unsere Salonprodukte nutzen. Wir arbeiten mit professionellen Marken wie: Maria Nila, O&M, Urban Alchemy, Olaplex, Goddess, evo. Alle Marken sind professionell, nachhaltig, 100% vegan, tierversuchsfrei.",
  },
  {
    question: "Kann ich Produkte auch an meine Kunden verkaufen?",
    answer: "Ja. Du musst kein eigenes Lager führen. Wenn du Produkte aus unserem Sortiment an deine Kunden verkaufst, erhältst du eine attraktive Gewinnbeteiligung. So hast du kein Lagerrisiko, keine Investition, aber trotzdem Verkaufs-Umsatz.",
  },
  {
    question: "Gibt es Schulungen oder Events?",
    answer: "Ja. Wenn du mit unseren Marken arbeitest, bekommst du auf Wunsch eine professionelle Produktschulung, damit du weiterhin als Experte für deine Kunden auftreten kannst. Zusätzlich finden regelmäßig Masterclasses, Events, internationale Education und Trainings von Marken und Top-Stylisten statt. Unser Salon ist gleichzeitig Education Center und Eventlocation. Vielleicht auch bald für deine eigene Masterclass!",
  },
  {
    question: "Welche Voraussetzungen muss ich erfüllen?",
    answer: "Du benötigst einen Friseurmeistertitel und einen Gewerbeschein. Beim Start unterstützen wir dich selbstverständlich bei allen organisatorischen Themen.",
  },
  {
    question: "Wie wirkt das Erlebnis für meine Kunden?",
    answer: "Sehr professionell. Der Salon ist im Premium-Design gestaltet mit hochwertigem Mobiliar, moderner Beleuchtung und verschiedenen Social-Media-Spots. Außerdem bieten wir 8 eigene Parkplätze, sehr gute Autobahnanbindung und zusätzliche kostenlose Parkmöglichkeiten in der Umgebung, da wir im Gewerbegebiet sind.",
  },
  {
    question: "Kann ich hier langfristig wachsen?",
    answer: "Absolut. Aktuell haben wir 6 Plätze, können aber bei Bedarf auf bis zu 12 Plätze erweitern. Du kannst jederzeit zusätzliche Tage buchen, einen zweiten Platz dazunehmen oder dein eigenes Team aufbauen – schließlich bist du selbstständig. JS Blond Coworking kann also mit deinem Business wachsen.",
  },
];

const CoworkingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    currentWork: "",
    hasCustomers: "",
    daysPerWeek: "",
    interests: [],
    message: ""
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useSeo({
    title: "Friseur Co-Working Heilbronn | Stuhlmiete & Salonplatz mieten | JS Blond Salon",
    description: "Selbstständig als Friseur:in in Heilbronn/Leingarten: Stuhlmiete & Co-Working im JS Blond Salon mit voll ausgestatteten Arbeitsplätzen, Premium-Produkten und flexibler Buchung. Jetzt anfragen.",
    path: "/coworking",
  });


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.currentWork || !formData.hasCustomers || !formData.daysPerWeek) {
      toast.error("Bitte fülle alle Pflichtfelder aus");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/coworking/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          current_work: formData.currentWork,
          has_customers: formData.hasCustomers,
          days_per_week: formData.daysPerWeek,
          interests: formData.interests,
          message: formData.message || null
        })
      });

      if (!response.ok) {
        throw new Error("Anfrage fehlgeschlagen");
      }

      toast.success("Danke für deine Anfrage! Wir melden uns bei dir.");
      setFormData({
        name: "",
        phone: "",
        email: "",
        currentWork: "",
        hasCustomers: "",
        daysPerWeek: "",
        interests: [],
        message: ""
      });
    } catch (error) {
      console.error("Application error:", error);
      toast.error("Ein Fehler ist aufgetreten. Bitte versuche es erneut.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-white">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="caption block mb-4">Co-Working</span>
              <h1
                className="text-4xl md:text-5xl font-normal tracking-tight mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Coworking für Friseurmeister
              </h1>
              <p className="text-xl leading-relaxed font-light text-[#2C2C2C]/80 mb-6">
                Arbeiten wie selbstständig – ohne den Stress, ohne eigenen Salon.
              </p>
              <p className="text-[#2C2C2C]/70 font-light leading-relaxed">
                Du bist Friseurmeister, arbeitest mobil oder bereits selbstständig und möchtest deine Kunden 
                in einem professionellen Salonumfeld bedienen – ohne die Verantwortung und Kosten eines eigenen Salons? 
                Bei JS Blond Coworking arbeitest du eigenständig mit deinen eigenen Kunden in einem modernen Salon 
                mit hochwertiger Ausstattung, inspirierender Atmosphäre und Austausch mit anderen Friseuren. 
                Du konzentrierst dich auf das, was du liebst: Haare. Wir kümmern uns um den Rest.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_1a92d488-6b73-421e-b552-cb97ed3d4201/artifacts/arzrg92d_IMG_8664.jpeg"
                alt="JS Blond Coworking Space – Bedienplätze im Premium Salon"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Was du bekommst */}
      <section className="section-spacing bg-[#F2EFE9]">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Was du bekommst
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex items-start gap-3 bg-white p-4 rounded-lg"
              >
                <CheckCircle size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" weight="fill" />
                <span className="text-[#2C2C2C]/80">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Salon Impressionen */}
      <section className="py-12 bg-white">
        <div className="container-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="overflow-hidden rounded-lg"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_1a92d488-6b73-421e-b552-cb97ed3d4201/artifacts/j46szm81_IMG_8661.jpeg"
                alt="JS Blond Salon – Waschplätze und Gesamtansicht"
                className="w-full h-[280px] object-cover hover:scale-105 transition-transform duration-500"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="overflow-hidden rounded-lg"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_1a92d488-6b73-421e-b552-cb97ed3d4201/artifacts/ux144oee_IMG_8663.jpeg"
                alt="JS Blond Salon – Premium Bedienplätze"
                className="w-full h-[280px] object-cover hover:scale-105 transition-transform duration-500"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* So funktioniert's */}
      <section className="section-spacing bg-white">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              So funktioniert's
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6 mb-8"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37] text-white rounded-full flex items-center justify-center font-bold">
                  {index + 1}
                </div>
                <div>
                  <h3 className="font-medium text-lg text-[#2C2C2C] mb-2">{step.title}</h3>
                  <p className="text-[#2C2C2C]/70">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Preisrechner */}
      <section className="section-spacing bg-[#F2EFE9]">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Was kostet mich das?
            </h2>
            <p className="text-[#2C2C2C]/70 max-w-2xl mx-auto">
              Berechne ganz einfach, was dich dein Bedienplatz monatlich kostet. 
              Je mehr Tage du buchst, desto günstiger wird der Preis pro Tag.
            </p>
          </motion.div>

          <div className="max-w-xl mx-auto">
            <PriceCalculator />
          </div>
        </div>
      </section>

      {/* Bewerbungsformular */}
      <section className="section-spacing bg-white" id="anfrage">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Jetzt Anfrage senden
            </h2>
            <p className="text-[#2C2C2C]/70 max-w-2xl mx-auto">
              Erzähl uns ein paar Dinge über dich und wie du dir dein Coworking vorstellst.
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                Name <span className="text-[#D4AF37]">*</span>
              </label>
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A8C73]" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Vor- und Nachname"
                  required
                  className="w-full pl-12 pr-4 py-3 border border-[#E6E2D6] rounded-lg focus:outline-none focus:border-[#D4AF37] transition-colors"
                />
              </div>
            </div>

            {/* Telefon & E-Mail */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Telefonnummer <span className="text-[#D4AF37]">*</span>
                </label>
                <div className="relative">
                  <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A8C73]" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Deine Telefonnummer"
                    required
                    className="w-full pl-12 pr-4 py-3 border border-[#E6E2D6] rounded-lg focus:outline-none focus:border-[#D4AF37] transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  E-Mail <span className="text-[#D4AF37]">*</span>
                </label>
                <div className="relative">
                  <Envelope size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A8C73]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Deine E-Mail"
                    required
                    className="w-full pl-12 pr-4 py-3 border border-[#E6E2D6] rounded-lg focus:outline-none focus:border-[#D4AF37] transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Wie arbeitest du aktuell? */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                Wie arbeitest du aktuell? <span className="text-[#D4AF37]">*</span>
              </label>
              <select
                name="currentWork"
                value={formData.currentWork}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-[#E6E2D6] rounded-lg focus:outline-none focus:border-[#D4AF37] transition-colors bg-white"
              >
                <option value="">Bitte wählen...</option>
                <option value="angestellt">Angestellt im Salon</option>
                <option value="mobil">Mobil bei Kunden zuhause</option>
                <option value="eigener-salon">Selbstständig im eigenen Salon</option>
                <option value="home-salon">Selbstständig im Home-Salon</option>
                <option value="vor-selbststaendigkeit">Friseurmeister kurz vor Selbstständigkeit</option>
              </select>
            </div>

            {/* Hast du bereits eigene Kunden? */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                Hast du bereits eigene Kunden? <span className="text-[#D4AF37]">*</span>
              </label>
              <div className="space-y-2">
                {[
                  { value: "stammkunden", label: "Ja, regelmäßige Stammkunden" },
                  { value: "einige", label: "Ein paar Kunden" },
                  { value: "noch-nicht", label: "Noch nicht" }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                      formData.hasCustomers === option.value
                        ? "border-[#D4AF37] bg-[#D4AF37]/5"
                        : "border-[#E6E2D6] hover:border-[#9A8C73]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="hasCustomers"
                      value={option.value}
                      checked={formData.hasCustomers === option.value}
                      onChange={handleInputChange}
                      className="accent-[#D4AF37]"
                    />
                    <span className="text-[#2C2C2C]">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Wie oft möchtest du im Salon arbeiten? */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                Wie oft möchtest du im Salon arbeiten? <span className="text-[#D4AF37]">*</span>
              </label>
              <select
                name="daysPerWeek"
                value={formData.daysPerWeek}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-[#E6E2D6] rounded-lg focus:outline-none focus:border-[#D4AF37] transition-colors bg-white"
              >
                <option value="">Bitte wählen...</option>
                <option value="1-2">1–2 Tage pro Woche</option>
                <option value="3-4">3–4 Tage pro Woche</option>
                <option value="5">5 Tage pro Woche</option>
                <option value="unsicher">Noch unsicher</option>
              </select>
            </div>

            {/* Was interessiert dich am Coworking am meisten? */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                Was interessiert dich am Coworking am meisten?
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  "Selbstständig arbeiten ohne eigenen Salon",
                  "Professionelles Salonumfeld",
                  "Austausch mit anderen Friseuren",
                  "Flexible Arbeitszeiten",
                  "Weniger Organisation / Risiko"
                ].map((interest) => (
                  <label
                    key={interest}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                      formData.interests.includes(interest)
                        ? "border-[#D4AF37] bg-[#D4AF37]/5"
                        : "border-[#E6E2D6] hover:border-[#9A8C73]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.interests.includes(interest)}
                      onChange={() => handleInterestChange(interest)}
                      className="accent-[#D4AF37]"
                    />
                    <span className="text-[#2C2C2C] text-sm">{interest}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Nachricht */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
                Nachricht <span className="text-[#9A8C73] text-xs">(optional)</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Erzähl uns kurz etwas über dich..."
                rows={4}
                className="w-full px-4 py-3 border border-[#E6E2D6] rounded-lg focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
              />
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#D4AF37] text-white py-4 rounded-full font-bold uppercase tracking-widest text-sm hover:bg-[#2C2C2C] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              whileHover={{ scale: isLoading ? 1 : 1.02 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
            >
              {isLoading ? (
                <>
                  <Spinner size={20} className="animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                "Anfrage absenden"
              )}
            </motion.button>

            {/* Info Text */}
            <div className="bg-[#F9F7F2] p-4 rounded-lg text-center">
              <p className="text-sm text-[#2C2C2C]/70">
                <strong className="text-[#2C2C2C]">So geht es weiter:</strong><br />
                Nach deiner Anfrage melden wir uns bei dir für ein kurzes Kennenlernen im Salon.
                Dabei zeigen wir dir den Coworking Space und schauen gemeinsam, ob das Konzept zu dir passt.
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing bg-white">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              FAQ – Coworking im JS Blond Salon
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.03 }}
                className="border border-[#E6E2D6] rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-[#F9F7F2] transition-colors"
                >
                  <span className="font-medium text-[#2C2C2C]">{faq.question}</span>
                  {openFaq === index ? (
                    <CaretUp size={20} className="text-[#9A8C73] flex-shrink-0" />
                  ) : (
                    <CaretDown size={20} className="text-[#9A8C73] flex-shrink-0" />
                  )}
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="px-4 pb-4 text-[#2C2C2C]/70 text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2C2C2C] text-white">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Haben wir noch Fragen offengelassen?
            </h2>
            <p className="text-white/70 font-light mb-8 max-w-xl mx-auto">
              Dann schreib uns gern eine Mail oder ruf uns an.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@js-blondsalon.de?subject=Co-Working Anfrage"
                data-testid="coworking-email-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#2C2C2C] px-8 py-4 rounded-full uppercase tracking-widest text-xs font-bold hover:bg-white transition-colors"
              >
                <Envelope size={18} />
                info@js-blondsalon.de
              </a>
              <a
                href="tel:+4917622968450"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full uppercase tracking-widest text-xs font-bold hover:bg-white hover:text-[#2C2C2C] transition-colors"
              >
                <Phone size={18} />
                0176 22968450
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoworkingPage;
